import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import Icons from "@/assets/icons";
import { Ionicons } from "@expo/vector-icons";
import { StripePaymentMethod } from "@/services/stripePaymentServices";

interface PaymentMethodCardProps {
  paymentMethod: StripePaymentMethod;
  onOptionsPress: (paymentMethod: StripePaymentMethod) => void;
}

const getCardBrandDisplay = (brand: string) => {
  const brandMap: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
    jcb: "JCB",
    diners: "Diners Club",
    unionpay: "UnionPay",
  };
  return brandMap[brand?.toLowerCase()] || brand || "Card";
};

const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
  paymentMethod,
  onOptionsPress,
}) => {
  return (
    <View style={styles.container}>
      <View style={styles.left}>
        <View style={styles.iconContainer}>
          <Icons.ProfileBank width={24} height={24} />
        </View>
        <View style={styles.info}>
          <Text style={styles.name}>
            {getCardBrandDisplay(paymentMethod.brand)} •••• {paymentMethod.last4}
          </Text>
          <Text style={styles.expiry}>
            Expires {paymentMethod.expMonth}/{paymentMethod.expYear}
          </Text>
        </View>
      </View>
      <View style={styles.right}>
        {paymentMethod.declined && (
          <View style={styles.declinedBadge}>
            <Text style={styles.declinedBadgeText}>Declined</Text>
          </View>
        )}
        {paymentMethod.isDefault && !paymentMethod.declined && (
          <View style={styles.defaultBadge}>
            <Text style={styles.defaultBadgeText}>Default</Text>
          </View>
        )}
        <TouchableOpacity
          style={styles.moreButton}
          onPress={() => onOptionsPress(paymentMethod)}
        >
          <Ionicons name="ellipsis-horizontal" size={20} color="#161D14" />
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default PaymentMethodCard;

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  left: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 12,
  },
  iconContainer: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  info: {
    flex: 1,
    gap: 4,
  },
  name: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  expiry: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
  },
  right: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  defaultBadge: {
    backgroundColor: "rgba(212, 142, 77, 0.2)",
    borderWidth: 1,
    borderColor: "#D48E4D",
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  defaultBadgeText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#D48E4D",
  },
  declinedBadge: {
    backgroundColor: "#FDE7D1",
    borderWidth: 1,
    borderColor: "#C12915",
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  declinedBadgeText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#C12915",
  },
  moreButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#EBEBEB",
    justifyContent: "center",
    alignItems: "center",
  },
});
